/**
 * OVERRIDE components/theme/ContentMetadataTags/ContentMetadataTags.jsx
 * REASON: Custom handling for meta tags
 * FILE:
 * FILE VERSION: VLT 5.0.1
 * PULL REQUEST:
 * TICKET: #1280, #1548
 * DATE: 2026-02-09
 * DEVELOPER: @skaeser, @thomas.schallert
 *
 * Depending on the nature of the change, if possible or suitable, mark the change with a JSX comment at the beginning and end of the change:
 *
 *   START CUSTOMIZATION
 *   <CUSTOMIZATION>
 *   END CUSTOMIZATION
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import {
  flattenToAppURL,
  isInternalURL,
  toPublicURL,
} from '@plone/volto/helpers/Url/Url';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import { getSchema } from '@plone/volto/actions/schema/schema';
import config from '@plone/volto/registry';
import { useDispatch } from 'react-redux';

// START CUSTOMIZATION
export const buildUrl = (base, query) => {
  if (!query || typeof query !== 'string') return base;

  const normalized = query.replace(/^[?&]+/, '').trim();
  if (!normalized) return base;

  const [urlWithoutHash, hash] = base.split('#');

  const separator = urlWithoutHash.includes('?') ? '&' : '?';
  const result = `${urlWithoutHash}${separator}${normalized}`;

  return hash ? `${result}#${hash}` : result;
};

const ContentMetadataTags = (props) => {
  const {
    opengraph_title,
    opengraph_description,
    seo_filter_page,
    seo_filter_page_whitelist,
    title,
    description,
  } = props.content;
  // START CUSTOMIZATION
  let {
    seo_title,
    seo_description,
    seo_canonical_url,
    seo_noindex = false,
    seo_nofollow = false,
  } = props.content;
  // END CUSTOMIZATION

  const dispatch = useDispatch();

  const getContentImageInfo = () => {
    const { contentMetadataTagsImageField } = config.settings;
    const image = props.content[contentMetadataTagsImageField];
    const { opengraph_image } = props.content;

    const contentImageInfo = {
      contentHasImage: false,
      url: null,
      height: null,
      width: null,
    };
    contentImageInfo.contentHasImage =
      opengraph_image?.scales?.large?.download ||
      image?.scales?.large?.download ||
      false;

    if (contentImageInfo.contentHasImage && opengraph_image?.scales?.large) {
      contentImageInfo.url = opengraph_image.scales.large.download;
      contentImageInfo.height = opengraph_image.scales.large.height;
      contentImageInfo.width = opengraph_image.scales.large.width;
    } else if (contentImageInfo.contentHasImage) {
      contentImageInfo.url = image.scales.large.download;
      contentImageInfo.height = image.scales.large.height;
      contentImageInfo.width = image.scales.large.width;
    }

    return contentImageInfo;
  };

  const contentImageInfo = getContentImageInfo();
  // START CUSTOMIZATION
  const location = useLocation();
  const content = props.content;
  const type = content?.['@type'];
  let seo_title_addition = ` | FHNW`;
  let canonicalUrl = null;
  const universityNavTitle = content?.['@components']?.university?.nav_title;
  const baseTitle = (seo_title || title || '').replace(/ FHNW$/, '');

  if (
    content['@type'] === 'Document' &&
    universityNavTitle &&
    !baseTitle.includes(universityNavTitle)
  ) {
    seo_title_addition = `: ${universityNavTitle}${seo_title_addition}`;
  }

  const parts = [];
  parts.push(baseTitle);
  parts.push(seo_title_addition);

  if (type === 'Contact' && content?.name_affix1) {
    parts.unshift(`${content.name_affix1} `);
  }

  seo_title = parts.join('').replace(/\u00AD/g, '');

  // Special handling for filter pages:
  // - If filters are applied, add noindex to prevent indexing of filtered pages.
  // - If the applied filters are whitelisted, do not add noindex and use individual
  //   title and description if available.
  const queryString =
    location.search
      .replaceAll(/(\?|&)(page=\d+|layout=[^&]+|sort_on=[^&]+)/g, '')
      .replace(/^(\?|&)/, '') || '';
  if (seo_filter_page) {
    if (queryString) {
      seo_noindex = true;
      if (seo_filter_page_whitelist?.length > 0) {
        seo_filter_page_whitelist.forEach((entry) => {
          if (entry.querystring !== queryString) return;
          seo_noindex = false;
          if (entry.title) {
            seo_title = `${entry.title}${seo_title_addition}`;
          } else if (entry.h1) {
            seo_title = `${entry.h1}${seo_title_addition}`;
          }
          if (entry.description) {
            seo_description = entry.description;
          }
        });
      }
    }
  }

  const publicUrl = toPublicURL(content['@id']);
  const selfReferenceOrCustomCanonical = seo_canonical_url || publicUrl;

  if (seo_filter_page) {
    const match = seo_filter_page_whitelist?.find(
      (entry) => entry.querystring === queryString,
    );

    if (match) {
      // Whitelisted filter page:
      // use custom canonical URL if configured, otherwise self-reference including query string.
      canonicalUrl = match.canonical_url || buildUrl(publicUrl, queryString);
    } else if (queryString) {
      // Non-whitelisted filter page with query:
      // no canonical URL to avoid indexing/filter combinations.
      canonicalUrl = null;
    } else {
      // Filter page without query:
      // use custom page canonical URL if configured, otherwise self-reference.
      canonicalUrl = selfReferenceOrCustomCanonical;
    }
  } else {
    canonicalUrl = selfReferenceOrCustomCanonical;
  }

  /**
   * Extracts the current language from content or URL
   *
   * @param {Object} content - The Plone content object
   * @param {Object} location - The React Router location object
   * @returns {string|null} The current language or null
   */
  const getCurrentLanguage = (content, location) => {
    // First try from content object
    if (content?.language?.token) {
      return content.language.token;
    }

    // Fallback: extract from URL
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const possibleLang = pathSegments[0];

    if (['de', 'en', 'fr'].includes(possibleLang)) {
      return possibleLang;
    }

    return null;
  };

  /**
   * Generates hreflang links for multilingual content using Volto URL helpers
   *
   * Follows SOLID principles:
   * - Single Responsibility: Only responsible for hreflang link generation
   * - Uses existing Volto URL helpers for consistency
   *
   * @param {Object} content - The Plone content object with translation information
   * @param {Object} location - The React Router location object
   * @param {Array} supportedLanguages - Supported languages in desired order
   * @param {string} defaultLanguage - Default language for x-default
   * @returns {Array} Array of link attribute objects for React Helmet
   */
  const generateHreflangLinks = (
    content,
    location,
    supportedLanguages = ['de', 'en', 'fr'],
    defaultLanguage = 'de',
  ) => {
    // Validate input parameters
    if (!content || !location) {
      return [];
    }

    // Extract translations from content object
    const translations = content?.['@components']?.translations?.items || [];

    // Add current page to translations if not already included
    const currentLanguage =
      content?.language?.token || getCurrentLanguage(content, location);
    const currentUrl = content?.['@id'] || location.pathname;

    // Check if current page is already in translations
    const currentPageInTranslations = translations.find(
      (t) => t.language === currentLanguage,
    );

    // Collect all available translations (including current page)
    const allTranslations = [...translations];
    if (
      !currentPageInTranslations &&
      currentLanguage &&
      supportedLanguages.includes(currentLanguage)
    ) {
      allTranslations.push({
        '@id': currentUrl,
        language: currentLanguage,
      });
    }

    if (allTranslations.length === 0) {
      return [];
    }

    // Convert translations to Map for fast access
    const translationMap = new Map();
    allTranslations.forEach((translation) => {
      const lang = translation.language;
      if (supportedLanguages.includes(lang)) {
        // Use Volto URL helpers for consistent URL handling
        let url;
        if (isInternalURL(translation['@id'])) {
          url = toPublicURL(flattenToAppURL(translation['@id']));
        } else {
          url = translation['@id'];
        }
        translationMap.set(lang, url);
      }
    });

    const hreflangLinks = [];

    // Generate links in desired order (de, en, fr)
    supportedLanguages.forEach((lang) => {
      if (translationMap.has(lang)) {
        const url = translationMap.get(lang);
        hreflangLinks.push({
          rel: 'alternate',
          hrefLang: lang,
          href: url,
          key: `hreflang-${lang}`,
          'data-react-helmet': 'true',
        });
      }
    });

    // Add x-default at the end
    let defaultUrl = null;

    // First try to find the default language
    if (translationMap.has(defaultLanguage)) {
      defaultUrl = translationMap.get(defaultLanguage);
    } else if (translationMap.size > 0) {
      // If no default language available, use first available language in desired order
      for (const lang of supportedLanguages) {
        if (translationMap.has(lang)) {
          defaultUrl = translationMap.get(lang);
          break;
        }
      }
    }

    if (defaultUrl) {
      // Always add x-default as last tag
      hreflangLinks.push({
        rel: 'alternate',
        hrefLang: 'x-default',
        href: defaultUrl,
        key: 'hreflang-x-default',
        'data-react-helmet': 'true',
      });
    }

    return hreflangLinks;
  };

  const hreflangLinks = React.useMemo(() => {
    return generateHreflangLinks(content, location);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, location]);

  React.useEffect(() => {
    content?.['@type'] &&
      !hasBlocksData(content) &&
      dispatch(getSchema(content['@type'], location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // END CUSTOMIZATION

  return (
    <>
      <Helmet>
        {/* START CUSTOMIZATION */}
        <title>{seo_title}</title>

        {(type === 'Contact' || type === 'TeamContact') &&
          content?.organisation_entities &&
          content?.organisation_entities.length > 0 && (
            <meta
              name="department"
              content={content?.organisation_entities
                ?.map((e) => e.title)
                .join(',')}
            />
          )}

        {/* END CUSTOMIZATION */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        <meta name="description" content={seo_description || description} />
        <meta
          property="og:title"
          content={opengraph_title || seo_title || title}
        />
        <meta property="og:url" content={toPublicURL(props.content['@id'])} />
        {/* START CUSTOMIZATION - robots meta tag with noindex/nofollow support */}
        <meta
          name="robots"
          content={`${seo_noindex ? 'noindex' : 'index'}, ${seo_nofollow ? 'nofollow' : 'follow'}`}
        />
        {/* END CUSTOMIZATION */}
        {contentImageInfo.contentHasImage && (
          <meta
            property="og:image"
            content={toPublicURL(contentImageInfo.url)}
          />
        )}
        {contentImageInfo.contentHasImage && (
          <meta property="og:image:width" content={contentImageInfo.width} />
        )}
        {contentImageInfo.contentHasImage && (
          <meta property="og:image:height" content={contentImageInfo.height} />
        )}
        {(opengraph_description || seo_description || description) && (
          <meta
            property="og:description"
            content={opengraph_description || seo_description || description}
          />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        {/* hreflang links for multilingual SEO using Volto URL helpers */}
        {hreflangLinks.map((linkProps) => (
          <link key={linkProps.key} {...linkProps} />
        ))}
      </Helmet>
    </>
  );
};

export default ContentMetadataTags;
